import { Data, TransformsList } from 'cio/utils/analytics/transforms';
import { isNone } from '@ember/utils';
import { capitalize } from 'cio/helpers/capitalize';
import {
  InAppExpirationType,
  inAppMessageDeliveryPriorityLabel,
  InAppMessageExpiration,
  InAppPageRule,
} from 'cio/utils/in-app/message-config';
import { Editor, InAppEditor } from 'cio/services/editor';
import { InAppStaticTemplate } from 'cio/services/in-app-static-templates';
import TemplateModel from 'cio/models/template';
import NewsletterModel from 'cio/models/newsletter';
import CampaignModel from 'cio/models/campaign';

type BuilderData = Data & {
  environmentId: string;
  editor: InAppEditor;
  message: CampaignModel | NewsletterModel;
  template: TemplateModel;
  templateToCopy?: TemplateModel | InAppStaticTemplate;
};

function getBuilderType(editor: InAppEditor) {
  return editor === Editor.VISUAL ? 'Visual' : 'Gist';
}

function getStartFrom(template?: TemplateModel | InAppStaticTemplate): string {
  if (template == null) {
    return 'Scratch';
  }

  if (template instanceof InAppStaticTemplate) {
    return 'Template';
  }

  return 'Existing';
}

function getTemplateName(template?: TemplateModel | InAppStaticTemplate): string {
  if (template == null) {
    return '';
  }

  if (template instanceof InAppStaticTemplate) {
    return template.name;
  }

  if (template.action != null) {
    return template.action.name;
  }

  if (template.newsletter != null) {
    return template.newsletter.name;
  }

  return '';
}

function getMessageType(message: CampaignModel | NewsletterModel) {
  if (message instanceof CampaignModel) {
    if (message.isApiTriggered) {
      return 'Triggered Broadcast';
    }

    return 'Campaign';
  }

  if (message instanceof NewsletterModel) {
    return 'Newsletter';
  }

  return 'Unknown';
}

function getParentDetails(data: BuilderData) {
  let { message, template, environmentId } = data;

  if (message instanceof CampaignModel) {
    return {
      'Campaign ID': `${environmentId}cam${message.id}`,
      'Campaign Action ID': `${environmentId}act${template.action?.id}`,
    };
  }

  if (message instanceof NewsletterModel) {
    return {
      'Newsletter ID': `${environmentId}new${message.id}`,
      'Newsletter Content ID': `${environmentId}con${template?.id}`,
    };
  }

  return {};
}

const Transforms: TransformsList = {
  editCompleted(data: Data) {
    let expiration = data.expiration as InAppMessageExpiration;
    let expirationDateType = 'Relative';
    if (!isNone(expiration) && expiration.type === InAppExpirationType.Absolute) {
      expirationDateType = 'Absolute';
    }
    let { persistent } = data;
    let priorityLabel = inAppMessageDeliveryPriorityLabel(data.priority);
    let positionLabel = capitalize([data.position]);
    let routeRules = data.routeRules as InAppPageRule[];
    let androidPresent = routeRules.some((rule) => rule.platform === 'android');
    let iosPresent = routeRules.some((rule) => rule.platform === 'ios');

    let pageRuleLabel =
      androidPresent && iosPresent ? 'Both' : androidPresent ? 'Android' : iosPresent ? 'iOS' : 'None';
    let fieldCount = data.attributes.length;
    let { attributesUsed, objectAttributesUsed } = getLiquidAttributesUsed(data.attributes);

    return {
      To: data.to,
      Priority: priorityLabel,
      'Expiration Date': expirationDateType,
      Position: positionLabel,
      'Page Rule': pageRuleLabel,
      'Page Rule Count': routeRules.length,
      'Field Count': fieldCount,
      'Attributes used in Liquid': attributesUsed,
      'Object Attributes used in Liquid': objectAttributesUsed,
      'Persist message': persistent,
      'Builder Type': getBuilderType(data.editor),
    };
  },

  builderChosen(data: BuilderData) {
    return {
      ...getParentDetails(data),
      'Builder Type': getBuilderType(data.editor),
      'Start From': getStartFrom(data?.templateToCopy),
      'Template Name': getTemplateName(data?.templateToCopy),
      'Message Used In': getMessageType(data.message),
    };
  },
};

// regex check for {{objects.object[array_index].key}} or {{customer.key}}
let regex = /{{objects.\w+\[\d+].\w+}}|{{customer.\w+}}/g;

function getLiquidAttributesUsed(attributes: any[]) {
  let objectAttributesUsed: string[] = [];
  let attributesUsed: string[] = [];

  for (let { value } of attributes) {
    for (let [liquidTag] of [...value.matchAll(regex)]) {
      let cleanLiquidPath = liquidTag.replaceAll(/{|}/g, '');
      if (cleanLiquidPath.includes('objects') === true) {
        objectAttributesUsed.push(cleanLiquidPath);
      } else {
        attributesUsed.push(cleanLiquidPath);
      }
    }
  }

  return {
    objectAttributesUsed,
    attributesUsed,
  };
}

export default Transforms;
